import {
  Trophy, CheckCircle, Flame, Zap, Target, Calculator, BarChart3,
  Triangle, Award, Star, Crown, Medal, Sparkles,
  BookOpen, Brain, Lightbulb, Rocket, Heart, Shield, Gem, Sword, Globe,
  GraduationCap, Clock, Moon, Sun, Layers, Gift, ChevronUp, Swords,
  BookMarked, FlameKindling, Atom, Leaf, Activity, PenLine, ScrollText,
  MessageCircle, MessageSquare, Users,
  type LucideIcon
} from "lucide-react";

export interface BadgeDefinition {
  type: string;
  name: string;
  description: string;
  hint: string;
  icon: LucideIcon;
  color: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  category: 'milestone' | 'streak' | 'mastery' | 'scholar' | 'social' | 'dedication' | 'review';
}

export const ALL_BADGES: BadgeDefinition[] = [

  // ── MILESTONES ── correct answer counts + XP + level ────────────────────────
  {
    type: 'first_correct',
    name: 'First Steps',
    description: 'Got your first correct answer',
    hint: 'Answer your first question correctly',
    icon: CheckCircle,
    color: 'bg-green-500',
    rarity: 'common',
    category: 'milestone',
  },
  {
    type: 'correct_10',
    name: 'Getting Started',
    description: '10 correct answers',
    hint: 'Answer 10 questions correctly',
    icon: Star,
    color: 'bg-blue-500',
    rarity: 'common',
    category: 'milestone',
  },
  {
    type: 'correct_25',
    name: 'Rising Star',
    description: '25 correct answers',
    hint: 'Answer 25 questions correctly',
    icon: Sparkles,
    color: 'bg-violet-500',
    rarity: 'common',
    category: 'milestone',
  },
  {
    type: 'correct_50',
    name: 'Half Century',
    description: '50 correct answers',
    hint: 'Answer 50 questions correctly',
    icon: Award,
    color: 'bg-orange-500',
    rarity: 'common',
    category: 'milestone',
  },
  {
    type: 'correct_100',
    name: 'Century Club',
    description: '100 correct answers',
    hint: 'Answer 100 questions correctly',
    icon: Trophy,
    color: 'bg-yellow-500',
    rarity: 'rare',
    category: 'milestone',
  },
  {
    type: 'correct_250',
    name: 'Champion',
    description: '250 correct answers',
    hint: 'Answer 250 questions correctly',
    icon: Crown,
    color: 'bg-pink-500',
    rarity: 'rare',
    category: 'milestone',
  },
  {
    type: 'correct_500',
    name: 'Legend',
    description: '500 correct answers',
    hint: 'Answer 500 questions correctly',
    icon: Gem,
    color: 'bg-cyan-500',
    rarity: 'epic',
    category: 'milestone',
  },
  {
    type: 'correct_1000',
    name: 'Elite',
    description: '1,000 correct answers',
    hint: 'Answer 1,000 questions correctly',
    icon: Sword,
    color: 'bg-indigo-600',
    rarity: 'epic',
    category: 'milestone',
  },
  {
    type: 'correct_2500',
    name: 'Grandmaster',
    description: '2,500 correct answers',
    hint: 'Answer 2,500 questions correctly',
    icon: Crown,
    color: 'bg-amber-600',
    rarity: 'legendary',
    category: 'milestone',
  },
  {
    type: 'xp_500',
    name: 'XP Rookie',
    description: 'Earned 500 total XP',
    hint: 'Earn 500 XP by answering questions',
    icon: Zap,
    color: 'bg-yellow-400',
    rarity: 'common',
    category: 'milestone',
  },
  {
    type: 'xp_2500',
    name: 'XP Climber',
    description: 'Earned 2,500 total XP',
    hint: 'Earn 2,500 total XP',
    icon: Zap,
    color: 'bg-orange-400',
    rarity: 'common',
    category: 'milestone',
  },
  {
    type: 'xp_10000',
    name: 'XP Hunter',
    description: 'Earned 10,000 total XP',
    hint: 'Earn 10,000 total XP',
    icon: Zap,
    color: 'bg-red-500',
    rarity: 'rare',
    category: 'milestone',
  },
  {
    type: 'xp_25000',
    name: 'XP Master',
    description: 'Earned 25,000 total XP',
    hint: 'Earn 25,000 total XP',
    icon: Rocket,
    color: 'bg-purple-600',
    rarity: 'epic',
    category: 'milestone',
  },
  {
    type: 'xp_50000',
    name: 'XP Legend',
    description: 'Earned 50,000 total XP',
    hint: 'Earn 50,000 total XP',
    icon: Rocket,
    color: 'bg-amber-600',
    rarity: 'legendary',
    category: 'milestone',
  },
  {
    type: 'level_5',
    name: 'Level Up',
    description: 'Reached Level 5',
    hint: 'Keep earning XP to reach Level 5',
    icon: ChevronUp,
    color: 'bg-green-600',
    rarity: 'common',
    category: 'milestone',
  },
  {
    type: 'level_10',
    name: 'Intermediate',
    description: 'Reached Level 10',
    hint: 'Keep earning XP to reach Level 10',
    icon: ChevronUp,
    color: 'bg-blue-600',
    rarity: 'rare',
    category: 'milestone',
  },
  {
    type: 'level_20',
    name: 'Advanced',
    description: 'Reached Level 20',
    hint: 'Keep earning XP to reach Level 20',
    icon: ChevronUp,
    color: 'bg-purple-600',
    rarity: 'epic',
    category: 'milestone',
  },
  {
    type: 'level_30',
    name: 'Elite Learner',
    description: 'Reached Level 30',
    hint: 'Keep earning XP to reach Level 30',
    icon: Crown,
    color: 'bg-amber-600',
    rarity: 'legendary',
    category: 'milestone',
  },

  // ── STREAKS ── in-session consecutive correct + daily study ─────────────────
  {
    type: 'streak_3',
    name: 'On Fire',
    description: '3 correct answers in a row',
    hint: 'Answer 3 questions correctly in a row',
    icon: Flame,
    color: 'bg-orange-400',
    rarity: 'common',
    category: 'streak',
  },
  {
    type: 'streak_5',
    name: 'Hot Streak',
    description: '5 correct answers in a row',
    hint: 'Answer 5 questions correctly in a row',
    icon: Flame,
    color: 'bg-red-500',
    rarity: 'common',
    category: 'streak',
  },
  {
    type: 'streak_10',
    name: 'Fire Storm',
    description: '10 correct answers in a row',
    hint: 'Answer 10 questions correctly in a row',
    icon: FlameKindling,
    color: 'bg-orange-600',
    rarity: 'rare',
    category: 'streak',
  },
  {
    type: 'streak_15',
    name: 'Unstoppable',
    description: '15 correct answers in a row',
    hint: 'Answer 15 questions correctly in a row',
    icon: Rocket,
    color: 'bg-red-600',
    rarity: 'epic',
    category: 'streak',
  },
  {
    type: 'streak_20',
    name: 'Perfect Storm',
    description: '20 correct answers in a row',
    hint: 'Answer 20 questions correctly in a row',
    icon: Zap,
    color: 'bg-purple-700',
    rarity: 'legendary',
    category: 'streak',
  },
  {
    type: 'daily_3',
    name: 'Three-Peat',
    description: 'Studied 3 days in a row',
    hint: 'Answer at least one question per day for 3 days straight',
    icon: Clock,
    color: 'bg-teal-500',
    rarity: 'common',
    category: 'streak',
  },
  {
    type: 'daily_7',
    name: 'Weekly Warrior',
    description: 'Answered questions 7 days in a row',
    hint: 'Answer at least one question per day for 7 days in a row',
    icon: Shield,
    color: 'bg-blue-600',
    rarity: 'rare',
    category: 'streak',
  },
  {
    type: 'daily_14',
    name: 'Fortnight Fighter',
    description: 'Answered questions 14 days in a row',
    hint: 'Answer at least one question per day for 14 days in a row',
    icon: Sword,
    color: 'bg-violet-600',
    rarity: 'epic',
    category: 'streak',
  },
  {
    type: 'daily_30',
    name: 'Monthly Master',
    description: 'Answered questions 30 days in a row',
    hint: 'Answer at least one question per day for 30 days in a row',
    icon: Trophy,
    color: 'bg-amber-600',
    rarity: 'legendary',
    category: 'streak',
  },

  // ── MASTERY ── topic / subject ───────────────────────────────────────────────
  {
    type: 'topic_master_number',
    name: 'Number Ninja',
    description: 'Mastered the Numbers topic',
    hint: 'Reach mastery in the Numbers topic in Math',
    icon: Calculator,
    color: 'bg-blue-500',
    rarity: 'rare',
    category: 'mastery',
  },
  {
    type: 'topic_master_measurement',
    name: 'Measurement Expert',
    description: 'Mastered the Measurement topic',
    hint: 'Reach mastery in the Measurement topic in Math',
    icon: Target,
    color: 'bg-green-500',
    rarity: 'rare',
    category: 'mastery',
  },
  {
    type: 'topic_master_geometry',
    name: 'Geometry Guru',
    description: 'Mastered the Geometry topic',
    hint: 'Reach mastery in the Geometry topic in Math',
    icon: Triangle,
    color: 'bg-purple-500',
    rarity: 'rare',
    category: 'mastery',
  },
  {
    type: 'topic_master_statistics',
    name: 'Stats Star',
    description: 'Mastered the Statistics topic',
    hint: 'Reach mastery in the Statistics topic in Math',
    icon: BarChart3,
    color: 'bg-orange-500',
    rarity: 'rare',
    category: 'mastery',
  },
  {
    type: 'topic_master_algebra',
    name: 'Algebra Ace',
    description: 'Mastered the Algebra topic',
    hint: 'Reach mastery in the Algebra topic in Math',
    icon: Calculator,
    color: 'bg-pink-500',
    rarity: 'rare',
    category: 'mastery',
  },
  {
    type: 'topic_master_fractions',
    name: 'Fraction Pro',
    description: 'Mastered the Fractions topic',
    hint: 'Reach mastery in the Fractions topic in Math',
    icon: Layers,
    color: 'bg-cyan-500',
    rarity: 'rare',
    category: 'mastery',
  },
  {
    type: 'topic_master_comprehension',
    name: 'Reading Expert',
    description: 'Mastered the Comprehension topic',
    hint: 'Reach mastery in the Comprehension topic in English',
    icon: BookOpen,
    color: 'bg-emerald-500',
    rarity: 'rare',
    category: 'mastery',
  },
  {
    type: 'topic_master_grammar',
    name: 'Grammar Guru',
    description: 'Mastered the Grammar topic',
    hint: 'Reach mastery in the Grammar topic in English',
    icon: PenLine,
    color: 'bg-indigo-500',
    rarity: 'rare',
    category: 'mastery',
  },
  {
    type: 'topic_master_vocabulary',
    name: 'Word Wizard',
    description: 'Mastered the Vocabulary topic',
    hint: 'Reach mastery in the Vocabulary topic in English',
    icon: ScrollText,
    color: 'bg-violet-500',
    rarity: 'rare',
    category: 'mastery',
  },
  {
    type: 'topic_master_living_things',
    name: 'Life Science Pro',
    description: 'Mastered the Living Things topic',
    hint: 'Reach mastery in the Living Things topic in Science',
    icon: Leaf,
    color: 'bg-green-600',
    rarity: 'rare',
    category: 'mastery',
  },
  {
    type: 'topic_master_matter',
    name: 'Matter Expert',
    description: 'Mastered the Matter topic',
    hint: 'Reach mastery in the Matter topic in Science',
    icon: Atom,
    color: 'bg-blue-600',
    rarity: 'rare',
    category: 'mastery',
  },
  {
    type: 'topic_master_energy',
    name: 'Energy Ace',
    description: 'Mastered the Energy topic',
    hint: 'Reach mastery in the Energy topic in Science',
    icon: Zap,
    color: 'bg-yellow-600',
    rarity: 'rare',
    category: 'mastery',
  },
  {
    type: 'subject_math',
    name: 'Math Master',
    description: 'Mastered all core Math topics',
    hint: 'Reach mastery in at least 4 different Math topics',
    icon: Brain,
    color: 'bg-blue-700',
    rarity: 'epic',
    category: 'mastery',
  },
  {
    type: 'subject_english',
    name: 'English Expert',
    description: 'Mastered all core English topics',
    hint: 'Reach mastery in at least 3 different English topics',
    icon: BookMarked,
    color: 'bg-emerald-700',
    rarity: 'epic',
    category: 'mastery',
  },
  {
    type: 'subject_science',
    name: 'Science Scholar',
    description: 'Mastered all core Science topics',
    hint: 'Reach mastery in at least 3 different Science topics',
    icon: Atom,
    color: 'bg-purple-700',
    rarity: 'epic',
    category: 'mastery',
  },
  {
    type: 'all_rounder',
    name: 'All-Rounder',
    description: 'Mastered topics across Math, English, and Science',
    hint: 'Earn the Math Master, English Expert, and Science Scholar badges',
    icon: Globe,
    color: 'bg-amber-700',
    rarity: 'legendary',
    category: 'mastery',
  },

  // ── SCHOLAR ── perfect scores, tests completed, reading ─────────────────────
  {
    type: 'first_perfect',
    name: 'Flawless',
    description: 'Scored 100% on a test',
    hint: 'Score 100% correct on any quiz or test',
    icon: Sparkles,
    color: 'bg-yellow-400',
    rarity: 'rare',
    category: 'scholar',
  },
  {
    type: 'perfect_5',
    name: 'Precision',
    description: '5 perfect scores',
    hint: 'Score 100% correct on 5 different quizzes',
    icon: Award,
    color: 'bg-orange-500',
    rarity: 'epic',
    category: 'scholar',
  },
  {
    type: 'perfect_10',
    name: 'Perfectionist',
    description: '10 perfect scores',
    hint: 'Score 100% correct on 10 different quizzes',
    icon: Trophy,
    color: 'bg-amber-600',
    rarity: 'legendary',
    category: 'scholar',
  },
  {
    type: 'tests_10',
    name: 'Test Warrior',
    description: 'Completed 10 tests',
    hint: 'Complete 10 quizzes or tests',
    icon: GraduationCap,
    color: 'bg-blue-500',
    rarity: 'rare',
    category: 'scholar',
  },
  {
    type: 'tests_25',
    name: 'Test Champion',
    description: 'Completed 25 tests',
    hint: 'Complete 25 quizzes or tests',
    icon: GraduationCap,
    color: 'bg-purple-600',
    rarity: 'epic',
    category: 'scholar',
  },
  {
    type: 'tests_50',
    name: 'Testing Legend',
    description: 'Completed 50 tests',
    hint: 'Complete 50 quizzes or tests',
    icon: GraduationCap,
    color: 'bg-amber-600',
    rarity: 'legendary',
    category: 'scholar',
  },
  {
    type: 'first_test',
    name: 'First Quiz',
    description: 'Completed your first full quiz',
    hint: 'Submit any quiz or test to earn this',
    icon: BookOpen,
    color: 'bg-teal-500',
    rarity: 'common',
    category: 'scholar',
  },
  {
    type: 'hard_challenger',
    name: 'Hard Challenger',
    description: 'Completed a quiz on Hard difficulty',
    hint: 'Submit a quiz using Hard difficulty',
    icon: Sword,
    color: 'bg-red-600',
    rarity: 'rare',
    category: 'scholar',
  },
  {
    type: 'subject_explorer',
    name: 'Subject Explorer',
    description: 'Answered questions in 3 different subjects',
    hint: 'Answer questions in Math, English, and Science',
    icon: Layers,
    color: 'bg-indigo-500',
    rarity: 'rare',
    category: 'scholar',
  },

  // ── SOCIAL ── challenges + leaderboard + prizes ──────────────────────────────
  {
    type: 'first_challenge',
    name: 'Game On',
    description: 'Sent your first challenge',
    hint: 'Challenge another student to a quiz',
    icon: Swords,
    color: 'bg-orange-500',
    rarity: 'common',
    category: 'social',
  },
  {
    type: 'challenge_won_1',
    name: 'Victory',
    description: 'Won your first challenge',
    hint: 'Beat an opponent in a quiz challenge',
    icon: Medal,
    color: 'bg-yellow-500',
    rarity: 'common',
    category: 'social',
  },
  {
    type: 'challenge_won_5',
    name: 'Battle Hardened',
    description: 'Won 5 challenges',
    hint: 'Win 5 quiz challenges against other students',
    icon: Shield,
    color: 'bg-orange-600',
    rarity: 'rare',
    category: 'social',
  },
  {
    type: 'challenge_won_10',
    name: 'Undefeated',
    description: 'Won 10 challenges',
    hint: 'Win 10 quiz challenges',
    icon: Crown,
    color: 'bg-red-600',
    rarity: 'epic',
    category: 'social',
  },
  {
    type: 'social_butterfly',
    name: 'Social Butterfly',
    description: 'Sent 10 challenge invites',
    hint: 'Send challenges to 10 different students',
    icon: Heart,
    color: 'bg-pink-500',
    rarity: 'rare',
    category: 'social',
  },
  {
    type: 'challenger_5',
    name: 'Challenger',
    description: 'Sent 5 challenge invites',
    hint: 'Challenge 5 other students to quiz battles',
    icon: Swords,
    color: 'bg-orange-500',
    rarity: 'common',
    category: 'social',
  },
  {
    type: 'challenger_25',
    name: 'Arena Regular',
    description: 'Sent 25 challenge invites',
    hint: 'Challenge 25 different students',
    icon: Swords,
    color: 'bg-red-500',
    rarity: 'rare',
    category: 'social',
  },
  {
    type: 'win_streak_3',
    name: 'Hat Trick',
    description: 'Won 3 challenges in a row',
    hint: 'Win 3 consecutive challenge matches',
    icon: Flame,
    color: 'bg-orange-500',
    rarity: 'rare',
    category: 'social',
  },
  {
    type: 'win_streak_5',
    name: 'Dominant',
    description: 'Won 5 challenges in a row',
    hint: 'Win 5 consecutive challenge matches',
    icon: Crown,
    color: 'bg-purple-600',
    rarity: 'epic',
    category: 'social',
  },
  {
    type: 'first_follower',
    name: 'Social Debut',
    description: 'Gained your first follower',
    hint: 'Have another student follow you',
    icon: Users,
    color: 'bg-sky-500',
    rarity: 'common',
    category: 'social',
  },
  {
    type: 'followers_10',
    name: 'Popular',
    description: 'Gained 10 followers',
    hint: 'Have 10 students follow you',
    icon: Star,
    color: 'bg-pink-500',
    rarity: 'rare',
    category: 'social',
  },
  {
    type: 'first_prize',
    name: 'Prize Winner',
    description: 'Won your first prize',
    hint: 'Complete tests to earn milestone prizes',
    icon: Gift,
    color: 'bg-pink-600',
    rarity: 'epic',
    category: 'social',
  },
  {
    type: 'first_message',
    name: 'First Message',
    description: 'Sent your first message to another student',
    hint: 'Open Messages and send a message to someone',
    icon: MessageCircle,
    color: 'bg-sky-500',
    rarity: 'common',
    category: 'social',
  },
  {
    type: 'pen_pal',
    name: 'Pen Pal',
    description: 'Messaged the same student 3 days in a row',
    hint: 'Keep a conversation going with the same person for 3 consecutive days',
    icon: MessageSquare,
    color: 'bg-teal-500',
    rarity: 'rare',
    category: 'social',
  },
  {
    type: 'social_connector',
    name: 'Social Connector',
    description: 'Had conversations with 3 different students',
    hint: 'Start conversations with 3 different students',
    icon: Users,
    color: 'bg-indigo-500',
    rarity: 'rare',
    category: 'social',
  },

  // ── DEDICATION ── habits + time-based ───────────────────────────────────────
  {
    type: 'early_bird',
    name: 'Early Bird',
    description: 'Studied before 7 AM',
    hint: 'Answer questions before 7:00 AM',
    icon: Sun,
    color: 'bg-yellow-400',
    rarity: 'rare',
    category: 'dedication',
  },
  {
    type: 'night_owl',
    name: 'Night Owl',
    description: 'Studied after 10 PM',
    hint: 'Answer questions after 10:00 PM',
    icon: Moon,
    color: 'bg-indigo-600',
    rarity: 'rare',
    category: 'dedication',
  },
  {
    type: 'multi_subject',
    name: 'Polymath',
    description: 'Studied 3 subjects in one day',
    hint: 'Answer questions in Math, English, and Science on the same day',
    icon: Layers,
    color: 'bg-teal-600',
    rarity: 'rare',
    category: 'dedication',
  },
  {
    type: 'comeback',
    name: 'Comeback Kid',
    description: 'Returned after being away',
    hint: 'Come back and study after being away for 3+ days',
    icon: Lightbulb,
    color: 'bg-amber-500',
    rarity: 'common',
    category: 'dedication',
  },
  {
    type: 'consistent_learner',
    name: 'Consistent Learner',
    description: '30-day question streak',
    hint: 'Answer at least one question per day for 30 consecutive days',
    icon: Star,
    color: 'bg-violet-500',
    rarity: 'legendary',
    category: 'dedication',
  },

  // ── WEEKLY LEADERBOARD ── competitive badges ──────────────────────────────
  {
    type: 'weekly_champion',
    name: 'Weekly Champion',
    description: 'Finished #1 on the weekly grade leaderboard',
    hint: 'Earn the most XP in your grade in a single week',
    icon: Crown,
    color: 'bg-yellow-500',
    rarity: 'epic',
    category: 'milestone',
  },
  {
    type: 'weekly_top3',
    name: 'Podium Finish',
    description: 'Finished in the top 3 on the weekly grade leaderboard',
    hint: 'Be in the top 3 XP earners in your grade for a week',
    icon: Medal,
    color: 'bg-orange-500',
    rarity: 'rare',
    category: 'milestone',
  },
  {
    type: 'weekly_top10',
    name: 'Top 10',
    description: 'Finished in the top 10 on the weekly grade leaderboard',
    hint: 'Be in the top 10 XP earners in your grade for a week',
    icon: Trophy,
    color: 'bg-blue-500',
    rarity: 'common',
    category: 'milestone',
  },
  {
    type: 'champion_5x',
    name: '5x Champion',
    description: 'Won the weekly leaderboard 5 times',
    hint: 'Finish #1 five different weeks',
    icon: Crown,
    color: 'bg-amber-600',
    rarity: 'legendary',
    category: 'milestone',
  },
  {
    type: 'champion_10x',
    name: '10x Champion',
    description: 'Won the weekly leaderboard 10 times',
    hint: 'Finish #1 ten different weeks',
    icon: Crown,
    color: 'bg-red-600',
    rarity: 'legendary',
    category: 'milestone',
  },
  {
    type: 'review_first',
    name: 'First Revision',
    description: 'Revised a lower grade for the first time',
    hint: 'Switch to Review and answer a question from the grade below',
    icon: BookMarked,
    color: 'bg-amber-500',
    rarity: 'common',
    category: 'review',
  },
  {
    type: 'review_25',
    name: 'Refresher',
    description: 'Answered 25 questions in Review',
    hint: 'Answer 25 review questions from the grade below',
    icon: BookOpen,
    color: 'bg-teal-500',
    rarity: 'common',
    category: 'review',
  },
  {
    type: 'review_100',
    name: 'Revision Pro',
    description: 'Answered 100 questions in Review',
    hint: 'Answer 100 review questions from the grade below',
    icon: GraduationCap,
    color: 'bg-indigo-500',
    rarity: 'rare',
    category: 'review',
  },
  {
    type: 'review_streak_3',
    name: 'Steady Reviser',
    description: 'Revised 3 days in a row',
    hint: 'Do a review session three days running',
    icon: Flame,
    color: 'bg-orange-500',
    rarity: 'rare',
    category: 'review',
  },
  {
    type: 'review_streak_7',
    name: 'Revision Habit',
    description: 'Revised 7 days in a row',
    hint: 'Do a review session seven days running',
    icon: FlameKindling,
    color: 'bg-rose-500',
    rarity: 'epic',
    category: 'review',
  },
];

export const RARITY_CONFIG = {
  common: {
    label: 'Common',
    textColor: 'text-slate-600 dark:text-slate-400',
    borderColor: 'border-slate-300 dark:border-slate-600',
    shadowClass: '',
  },
  rare: {
    label: 'Rare',
    textColor: 'text-blue-600 dark:text-blue-400',
    borderColor: 'border-blue-400 dark:border-blue-500',
    shadowClass: 'shadow-blue-500/30',
  },
  epic: {
    label: 'Epic',
    textColor: 'text-orange-600 dark:text-orange-400',
    borderColor: 'border-orange-400 dark:border-orange-500',
    shadowClass: 'shadow-orange-500/40',
  },
  legendary: {
    label: 'Legendary',
    textColor: 'text-amber-600 dark:text-amber-400',
    borderColor: 'border-amber-500 dark:border-amber-400',
    shadowClass: 'shadow-amber-500/50',
  },
} as const;

export const getBadgeByType = (type: string): BadgeDefinition | undefined => {
  return ALL_BADGES.find(badge => badge.type === type);
};

export const getBadgesByCategory = (category: BadgeDefinition['category']): BadgeDefinition[] => {
  return ALL_BADGES.filter(badge => badge.category === category);
};

export const createAchievementTypeMap = (): Record<string, Omit<BadgeDefinition, 'type' | 'category'>> => {
  return ALL_BADGES.reduce((acc, badge) => {
    acc[badge.type] = {
      name: badge.name,
      description: badge.description,
      hint: badge.hint,
      icon: badge.icon,
      color: badge.color,
      rarity: badge.rarity,
    };
    return acc;
  }, {} as Record<string, Omit<BadgeDefinition, 'type' | 'category'>>);
};

/**
 * Design system: a badge's colour is a tint on a small icon square, never a filled
 * gradient circle. Classes are written out in full so Tailwind's scanner sees them.
 */
const BADGE_TINTS: Record<string, string> = {
  amber:   'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  blue:    'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  cyan:    'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400',
  emerald: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  green:   'bg-green-500/10 text-green-600 dark:text-green-400',
  indigo:  'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
  orange:  'bg-orange-500/10 text-orange-600 dark:text-orange-400',
  pink:    'bg-pink-500/10 text-pink-600 dark:text-pink-400',
  purple:  'bg-purple-500/10 text-purple-600 dark:text-purple-400',
  red:     'bg-red-500/10 text-red-600 dark:text-red-400',
  rose:    'bg-rose-500/10 text-rose-600 dark:text-rose-400',
  sky:     'bg-sky-500/10 text-sky-600 dark:text-sky-400',
  teal:    'bg-teal-500/10 text-teal-600 dark:text-teal-400',
  violet:  'bg-violet-500/10 text-violet-600 dark:text-violet-400',
  yellow:  'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
};

/** Turns a badge's `bg-<hue>-<shade>` colour into a flat icon-square tint. */
export function badgeTint(color: string): string {
  const hue = color.replace(/^bg-/, '').split('-')[0];
  return BADGE_TINTS[hue] ?? 'bg-primary/10 text-primary';
}
